import get from 'lodash/get'

import store from '@/store'
import router from '@/router'

import remoteConfig from '@/lib/remote-config'
import { NEXT_LESSON, PREV_LESSON } from '@/store/modules/lesson/types'
import { GENERATE_RANDOM_QUIZ } from '@/store/modules/quiz/types'
import {
  quiz_enabled,
  masterclass_chaining,
  masterclass_redirect_to_home
} from '@/lib/remote-config/constants'

/**
 * Get the next router argument given the current position
 * @param {string} lessonId
 * @param {string} lessonStepId
 * @param {string} masterclassId
 * @param {boolean} [redirectToQuiz=false]
 */
export function goToNextLessonScreen(
  lessonId,
  lessonStepId,
  masterclassId,
  redirectToQuiz = false
) {
  const masterclass = getContentOptimized('getThemeById', masterclassId)
  const lessonStep = getContent('lessonStep', lessonStepId)
  const lesson = getContent('lesson', lessonId)

  // 1. Test if we should go to the next lesson
  if (lesson) {
    const lessonIndex = lessonStep.lessons.findIndex(lessonId => lessonId === lesson.id)

    if (lessonIndex < lessonStep.lessons.length - 1) {
      store.dispatch(NEXT_LESSON)
      window.scrollTo(0, 0)

      return
      // 1.1 If we have a quiz, generate it and redirect to it
    } else if (redirectToQuiz && lessonStep.quizId && remoteConfig.getBoolean(quiz_enabled)) {
      store.dispatch(GENERATE_RANDOM_QUIZ, {
        contentType: 'lessonStep',
        contentId: lessonStep.id
      })

      return router.push({
        name: 'quiz',
        params: {
          contentType: 'lessonStep',
          contentId: lessonStep.id,
          quizId: lessonStep.quizId
        }
      })
    }
  }

  // 2. Test if we should go to the next lesson step
  const lessonStepIndex = masterclass.lessonSteps.findIndex(step => step.id === lessonStep.id)

  if (lessonStepIndex < masterclass.lessonSteps.length - 1) {
    let nextLessonStep = masterclass.lessonSteps[lessonStepIndex + 1]
    router.push({
      name: 'lesson',
      params: {
        themeId: masterclass.id,
        lessonStepId: nextLessonStep.id,
        lessonId: nextLessonStep.lessons[0].id
      }
    })

    window.scrollTo(0, 0)

    return
  }

  // 3. Test if we should go to the next masterclass
  if (lessonStepIndex === masterclass.lessonSteps.length - 1) {
    const nextMasterclass = getNextMasterclass(masterclass.order)
    
    if (masterclass.popupRecommendations === true)
      localStorage.setItem('triggerModal', true)

    // 3.1 If there is no more content, go to home
    if (!remoteConfig.getBoolean(masterclass_chaining) || !nextMasterclass) {
      if (remoteConfig.getBoolean(masterclass_redirect_to_home)) {
        return router.push({ name: 'home' })
      } else {
        localStorage.setItem('triggerModal', true)
        return router.push({ name: 'themeList' })
      }
    }

    const nextLessonStep = getContent('lessonStep', nextMasterclass.lessonSteps[0].id)

    router.push({
      name: 'lesson',
      params: {
        themeId: nextMasterclass.id,
        lessonStepId: nextLessonStep.id,
        lessonId: nextLessonStep.lessons[0]
      }
    })

    window.scrollTo(0, 0)

    return
  }
}

/**
 * Get the next router argument given the current position
 * @param {string} lessonId
 * @param {string} lessonStepId
 * @param {string} masterclassId
 */
export function goToPrevLessonScreen(lessonId, lessonStepId, masterclassId) {
  const masterclass = getContentOptimized('getThemeById', masterclassId)
  const lessonStep = getContent('lessonStep', lessonStepId)
  const lesson = getContent('lesson', lessonId)

  // 1. Test if we should go to the previous lesson
  if (lesson) {
    const lessonIndex = lessonStep.lessons.findIndex(lessonId => lessonId === lesson.id)

    if (lessonIndex > 0) {
      store.dispatch(PREV_LESSON)
      window.scrollTo(0, 0)

      return
    }
  }

  // 2. Test if we should go to the previous lesson step
  const lessonStepIndex = masterclass.lessonSteps.findIndex(step => step.id === lessonStep.id)

  if (lessonStepIndex > 0) {
    let nextLessonStep = getContent('lessonStep', masterclass.lessonSteps[lessonStepIndex - 1].id)

    router.push({
      name: 'lesson',
      params: {
        themeId: masterclass.id,
        lessonStepId: nextLessonStep.id,
        lessonId: nextLessonStep.lessons[nextLessonStep.lessons.length - 1]
      }
    })

    window.scrollTo(0, 0)

    return
  }

  // 3. Test if we should go to the previous masterclass
  if (lessonStepIndex === 0) {
    const prevMasterclass = getPrevMasterclass(masterclass.order)

    // 3.1 If there is no more content, go to home
    if (!remoteConfig.getBoolean(masterclass_chaining) || !prevMasterclass) {
      if (remoteConfig.getBoolean(masterclass_redirect_to_home)) {
        return router.push({ name: 'home' })
      } else {
        return router.push({ name: 'themeList' })
      }
    }

    const prevLessonStep = getContent(
      'lessonStep',
      prevMasterclass.lessonSteps[prevMasterclass.lessonSteps.length - 1].id
    )

    router.push({
      name: 'lesson',
      params: {
        themeId: prevMasterclass.id,
        lessonStepId: prevLessonStep.id,
        lessonId: prevLessonStep.lessons[prevLessonStep.lessons.length - 1]
      }
    })

    window.scrollTo(0, 0)

    return
  }
}

/**
 * Get the content
 * @param {string} contentType
 * @param {string} contentId
 */
function getContent(contentType, contentId) {
  return get(store.state, ['lesson', contentType, 'byId', contentId], null)
}

/**
 * Get the content based on a getter
 * @param {string} getterToUse
 * @param {string} contentId
 */
function getContentOptimized(getterToUse, contentId) {
  return store.getters[getterToUse](contentId)
}

/**
 * Get the next masterclass based on the order
 * @param {number} order
 * @returns {Masterclass}
 */
function getNextMasterclass(order) {
  return store.getters.getThemes
    .map(theme => store.getters.getThemeById(theme.id))
    .find(theme => theme && theme.order === order + 1)
}

/**
 * Get the previous masterclass based on the order
 * @param {number} order
 * @returns {Masterclass}
 */
function getPrevMasterclass(order) {
  if (order <= 0) return null

  return store.getters.getThemes
    .map(theme => store.getters.getThemeById(theme.id))
    .find(theme => theme && theme.order === order - 1)
}
